import { Link } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { KeyRound } from 'lucide-react'
import { useAIAgents } from '@/api/hooks'
import { formatEventTime } from '@/lib/utils'

export function AIAgentsPage() {
  const { data: agents, isLoading } = useAIAgents()

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6">
        <h1 className="text-2xl font-bold tracking-tight">AI Agents</h1>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold tracking-tight">AI Agents</h1>
        <Button variant="outline" asChild>
          <Link to="/ai/api-keys">
            <KeyRound className="mr-2 h-4 w-4" /> Manage API Keys
          </Link>
        </Button>
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {agents?.map((agent) => (
          <Link key={agent.key} to="/ai/$agentKey" params={{ agentKey: agent.key }}>
            <Card className="h-full cursor-pointer transition-colors hover:bg-muted/50">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{agent.name}</CardTitle>
                  <Badge variant={agent.enabled ? 'default' : 'secondary'}>
                    {agent.enabled ? 'Active' : 'Disabled'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {agent.description && (
                  <p className="line-clamp-2 text-sm text-muted-foreground">
                    {agent.description}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  <span className="font-mono text-foreground">{agent.runtime_model}</span> · Updated{' '}
                  {formatEventTime(agent.updated_at)}
                </p>
                {agent.category && agent.category !== 'general' && (
                  <Badge variant="outline" className="text-xs capitalize">
                    {agent.category}
                  </Badge>
                )}
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  )
}
